import { useCallback, useEffect, useRef, useState } from "react";
import { DotLottie, DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useGameStore } from "@/state/gameStore";
import { useCoverTransform } from "@/hooks/useCoverTransform";

import transitionLottie from "@/assets/lottie/room-switch.lottie";
import { StrapiMedia } from "@/components/shared/StrapiMedia";
import { useVideoCacheStore } from "@/hooks/useVideoCacheStore";
import { useNextPhase } from "@/state/selectors";
import { PhaseTransitionPreloadStatus } from "@/libs/assetPreloading/usePreloadPhaseTransition";
import { Message } from "@/types/api";

const LOTTIE_W = 2160;
const LOTTIE_H = 2160;
const ZOOM_START_SCALE = 0.58;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getMarkerName(marker: { name: string }): string {
    try {
        return JSON.parse(marker.name);
    } catch {
        return marker.name;
    }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TransitionStage = "waiting" | "playing" | "done";

type PhaseTransitionOverlayProps = {
    preloadStatus: PhaseTransitionPreloadStatus;
    onComplete: () => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PhaseTransitionOverlay({ preloadStatus, onComplete }: PhaseTransitionOverlayProps) {
    const [stage, setStage] = useState<TransitionStage>("waiting");
    const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);

    // --- Store data ---
    const nextRoom = useGameStore((s) => s.nextRoom);
    const nextRoomVariant = useGameStore((s) => s.nextRoomVariant);
    const completePhase = useGameStore((s) => s.completePhase);
    const startPhase = useGameStore((s) => s.startPhase);
    const selectRoom = useGameStore((s) => s.selectRoom);
    const { preloadVideos } = useVideoCacheStore();

    const nextPhase = useNextPhase();

    // --- Refs ---
    const containerRef = useRef<HTMLDivElement>(null);
    const lottieContainerRef = useRef<HTMLDivElement>(null);
    const roomImgRef = useRef<HTMLDivElement>(null);
    const nextPhaseRef = useRef(nextPhase);
    const hasSwapped = useRef(false);
    const imageRevealed = useRef(false);

    const lockedNextPhase = nextPhaseRef.current
    // Stable ref for the phase swap so the rAF effect doesn't depend on store selectors
    const swapRef = useRef(() => {});
    swapRef.current = () => {
        if (!lockedNextPhase || !nextRoomVariant) return;
        completePhase();
        startPhase(lockedNextPhase.documentId, lockedNextPhase.messages.filter((m): m is Message => m != null) || []);
        selectRoom(nextRoomVariant);
    };

    const lottieCover = useCoverTransform(lottieContainerRef, LOTTIE_W, LOTTIE_H);

    const handleDotLottieLoaded = useCallback((instance: DotLottie | null) => {
        if (!instance) return;

        const onLoad = () => setDotLottie(instance);

        instance.addEventListener("load", onLoad);

        if (instance.isLoaded) {
            onLoad();
        }
    }, []);

    // Save next phase to a ref, because it changes when startPhase is called in the middle of the animation.
    // When transitioning to the last phase, nextPhase becomes null immediately, which would stop the animation because
    // of the early return.
    useEffect(() => {
        if (stage === "playing" && nextPhase) {
            nextPhaseRef.current = nextPhase;
        }
    }, [stage, nextPhase]);

    // ----- Preload next room video -----
    useEffect(() => {
        const video = nextRoom?.animated;
        if (video) {
            preloadVideos([video]);
        }
    }, [nextRoom, preloadVideos]);

    // ----- Stage: waiting → playing -----
    useEffect(() => {
        if (stage === "waiting" && preloadStatus === "ready") {
            setStage("playing");
        }
    }, [stage, preloadStatus]);

    // ----- Single rAF loop -----
    useEffect(() => {
        if (!dotLottie || stage !== "playing") return;

        let rafId: number;
        let zoomStartTime: number | null = null;
        let zoomDurationMs = 600;
        let markersResolved = false;

        let closedFrame: number | null = null;
        let openFrame: number | null = null;
        let zoomFrame: number | null = null;

        const resolveMarkers = (): boolean => {
            const markers = dotLottie.markers();
            if (!markers?.length) return false;

            const closed = markers.find((m) => getMarkerName(m) === "cutout_closed");
            const open = markers.find((m) => getMarkerName(m) === "cutout_open");
            const zoom = markers.find((m) => getMarkerName(m) === "zoom_start");

            closedFrame = closed?.time ?? null;
            openFrame = open?.time ?? null;
            zoomFrame = zoom?.time ?? null;

            if (zoomFrame !== null) {
                const totalFrames = dotLottie.totalFrames;
                const fps = (dotLottie.speed || 1) * 60;
                const remainingFrames = totalFrames - zoomFrame;
                const remainingMs = (remainingFrames / fps) * 1000;
                zoomDurationMs = Math.max(100, remainingMs * 0.8 - 500);
            }

            return true;
        };

        const ease = (t: number) => 1 - Math.pow(1 - t, 3);
        const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

        const tick = (timestamp: number) => {
            if (!markersResolved) {
                markersResolved = resolveMarkers();
                if (!markersResolved) {
                    rafId = requestAnimationFrame(tick);
                    return;
                }
            }

            const currentFrame = dotLottie.currentFrame;
            const imgEl = roomImgRef.current;
            const container = containerRef.current;
     /*       // --- DEBUG: pause ~60 frames after cutout_open ---
            if (openFrame !== null && currentFrame >= openFrame + 10) {
                dotLottie.pause();
                return;
            }*/
            // --- Swap store at cutout_closed ---
            if (closedFrame !== null && currentFrame >= closedFrame && !hasSwapped.current) {
                hasSwapped.current = true;
                swapRef.current();
            }

            // --- Reveal image at cutout_open ---
            if (openFrame !== null && currentFrame >= openFrame && !imageRevealed.current && imgEl && container) {
                imageRevealed.current = true;
                const cw = container.clientWidth;
                const ch = container.clientHeight;
                const startW = cw * ZOOM_START_SCALE;
                const startH = ch * ZOOM_START_SCALE;
                imgEl.style.left = `${(cw - startW) / 2}px`;
                imgEl.style.top = `${(ch - startH) / 2}px`;
                imgEl.style.width = `${startW}px`;
                imgEl.style.height = `${startH}px`;
                imgEl.style.borderRadius = "12px";
                imgEl.style.visibility = "visible";
            }

            // --- Zoom at zoom_start ---
            if (zoomFrame !== null && currentFrame >= zoomFrame && container && imgEl) {
                if (!zoomStartTime) zoomStartTime = timestamp;

                const cw = container.clientWidth;
                const ch = container.clientHeight;
                const startW = cw * ZOOM_START_SCALE;
                const startH = ch * ZOOM_START_SCALE;

                const elapsed = timestamp - zoomStartTime;
                const rawT = Math.min(elapsed / zoomDurationMs, 1);
                const t = ease(rawT);

                imgEl.style.left = `${lerp((cw - startW) / 2, 0, t)}px`;
                imgEl.style.top = `${lerp((ch - startH) / 2, 0, t)}px`;
                imgEl.style.width = `${lerp(startW, cw, t)}px`;
                imgEl.style.height = `${lerp(startH, ch, t)}px`;
                imgEl.style.borderRadius = `${lerp(12, 0, t)}px`;

                if (rawT >= 1) {
                    setStage("done");
                    return;
                }
            }

            rafId = requestAnimationFrame(tick);
        };

        const startLoop = () => {
            rafId = requestAnimationFrame(tick);
        };

        if (dotLottie.isPlaying) {
            startLoop();
        } else {
            dotLottie.addEventListener("play", startLoop);
        }

        return () => {
            cancelAnimationFrame(rafId);
            dotLottie.removeEventListener("play", startLoop);
        };
    }, [dotLottie, stage]);

    // ----- Stage: done → notify parent -----
    useEffect(() => {
        if (stage === "done") {
            onComplete();
        }
    }, [stage, onComplete]);

    if (!lockedNextPhase || !nextRoom) return null;

    const showLottie = stage === "playing";

    return (
        <div
            ref={containerRef}
            style={{
                position: "absolute",
                inset: 0,
                zIndex: 9999,
                pointerEvents: "none",
            }}
        >
            {/* Next room image — starts hidden, revealed at cutout_open, zooms to fullscreen */}
            {nextRoom.image && (
                <div
                    ref={roomImgRef}
                    style={{
                        position: "absolute",
                        visibility: "hidden",
                        zIndex: 0,
                        overflow: "hidden",
                    }}
                >
                    <StrapiMedia
                        size="lg"
                        image={nextRoom.image}
                        alt={nextRoom.name ?? "Room"}
                        className="h-full w-full object-cover"
                    />
                </div>
            )}

            {/* Lottie container — always mounted for useCoverTransform */}
            <div
                ref={lottieContainerRef}
                style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    overflow: "hidden",
                    visibility: showLottie ? "visible" : "hidden",
                }}
            >
                {showLottie && (
                    <div
                        style={{
                            position: "absolute",
                            left: lottieCover.offsetX,
                            top: lottieCover.offsetY,
                            width: LOTTIE_W * lottieCover.scale,
                            height: LOTTIE_H * lottieCover.scale,
                        }}
                    >
                        <DotLottieReact
                            src={transitionLottie}
                            autoplay={true}
                            loop={false}
                            style={{ width: "100%", height: "100%" }}
                            dotLottieRefCallback={handleDotLottieLoaded}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
